import { DollarSign, TrendingUp, Users, CreditCard } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';

interface Transaction {
  id: string;
  studentName: string;
  course: string;
  amount: number;
  status: 'Completed' | 'Pending' | 'Failed';
  date: string;
}

export function PaymentsSection() {
  const transactions: Transaction[] = [
    {
      id: 'TXN001',
      studentName: 'John Doe',
      course: 'Web Development Fundamentals',
      amount: 299,
      status: 'Completed',
      date: '2026-04-01',
    },
    {
      id: 'TXN002',
      studentName: 'Jane Smith',
      course: 'UI/UX Design Principles',
      amount: 199,
      status: 'Completed',
      date: '2026-04-02',
    },
    {
      id: 'TXN003',
      studentName: 'Mike Johnson',
      course: 'Web Development Fundamentals',
      amount: 299,
      status: 'Pending',
      date: '2026-04-03',
    },
    {
      id: 'TXN004',
      studentName: 'David Brown',
      course: 'Web Development Fundamentals',
      amount: 299,
      status: 'Completed',
      date: '2026-04-03',
    },
    {
      id: 'TXN005',
      studentName: 'Sarah Williams',
      course: 'Python Programming',
      amount: 249,
      status: 'Failed',
      date: '2026-04-04',
    },
    {
      id: 'TXN006',
      studentName: 'Emily Davis',
      course: 'Python Programming',
      amount: 249,
      status: 'Completed',
      date: '2026-04-04',
    },
  ];

  const totalRevenue = transactions
    .filter((t) => t.status === 'Completed')
    .reduce((acc, t) => acc + t.amount, 0);

  const pendingAmount = transactions
    .filter((t) => t.status === 'Pending')
    .reduce((acc, t) => acc + t.amount, 0);

  const completedTransactions = transactions.filter((t) => t.status === 'Completed').length;

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Completed':
        return 'bg-green-100 text-green-700';
      case 'Pending':
        return 'bg-yellow-100 text-yellow-700';
      case 'Failed':
        return 'bg-red-100 text-red-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold">Payments</h2>
        <p className="text-gray-500 mt-1">Revenue and transaction overview</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total Revenue</p>
                <p className="text-2xl font-bold">${totalRevenue.toLocaleString()}</p>
              </div>
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                <DollarSign className="w-6 h-6 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Pending Amount</p>
                <p className="text-2xl font-bold">${pendingAmount.toLocaleString()}</p>
              </div>
              <div className="w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-yellow-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Transactions</p>
                <p className="text-2xl font-bold">{completedTransactions}</p>
              </div>
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                <CreditCard className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Paying Students</p>
                <p className="text-2xl font-bold">{new Set(transactions.map((t) => t.studentName)).size}</p>
              </div>
              <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center">
                <Users className="w-6 h-6 text-purple-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Recent Transactions</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="border border-gray-200 rounded-lg overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Transaction ID</TableHead>
                  <TableHead>Student</TableHead>
                  <TableHead>Course</TableHead>
                  <TableHead>Amount</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Date</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {transactions.map((transaction) => (
                  <TableRow key={transaction.id}>
                    <TableCell className="font-mono text-sm">{transaction.id}</TableCell>
                    <TableCell className="font-medium">{transaction.studentName}</TableCell>
                    <TableCell className="text-sm text-gray-600">{transaction.course}</TableCell>
                    <TableCell className="font-semibold">${transaction.amount}</TableCell>
                    <TableCell>
                      <Badge className={getStatusColor(transaction.status)}>{transaction.status}</Badge>
                    </TableCell>
                    <TableCell className="text-sm text-gray-600">
                      {new Date(transaction.date).toLocaleDateString()}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Revenue by Course</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {Array.from(new Set(transactions.map((t) => t.course))).map((course) => {
              const courseRevenue = transactions
                .filter((t) => t.course === course && t.status === 'Completed')
                .reduce((acc, t) => acc + t.amount, 0);
              const enrollments = transactions.filter((t) => t.course === course).length;

              return (
                <div key={course} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                  <div>
                    <p className="font-medium">{course}</p>
                    <p className="text-sm text-gray-500">{enrollments} enrollments</p>
                  </div>
                  <div className="text-right">
                    <p className="text-xl font-bold text-green-600">${courseRevenue.toLocaleString()}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
